import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Check, GraduationCap, Lock, Trophy, Share2 } from "lucide-react";
import { useStreak } from "@/context/StreakContext";
import { ShareStreak } from "@/components/ShareStreak";

export const StreakFlame = () => {
  const { streak, board } = useStreak();
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  if (!streak) return null;

  const n = streak.current;
  const hot = n > 0 && streak.active_today;

  return (
    <div className="relative">
      <button
        data-testid="streak-flame"
        aria-label={`Chat streak: ${n} ${n === 1 ? "day" : "days"}`}
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-[40px] items-center gap-1.5 rounded-full border px-3 text-sm font-semibold transition-colors ${
          hot
            ? "border-orange-400/40 bg-orange-400/10 text-orange-300 hover:border-orange-400/70"
            : "border-white/10 bg-panel text-ink-soft hover:text-ink"
        }`}
      >
        <Flame className={`h-4 w-4 ${hot ? "streak-flicker text-orange-400" : ""}`} />
        <span data-testid="streak-count">{n}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="streak-panel"
            className="rise absolute end-0 top-12 z-50 w-[19rem] rounded-3xl border border-white/10 bg-surface/95 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl"
          >
            <p className="font-display text-lg font-bold tracking-tight">
              {n === 0 ? "Start a streak today" : `${n}-day chat streak`}
            </p>
            <p className="mt-1 text-sm text-ink-soft" data-testid="streak-next">
              {streak.next
                ? `${streak.next.remaining} more ${
                    streak.next.remaining === 1 ? "day" : "days"
                  } → ${streak.next.label}`
                : "Every reward unlocked. You are unstoppable."}
            </p>
            {!streak.active_today && n > 0 && (
              <p className="mt-2 text-xs text-orange-300" data-testid="streak-at-risk">
                Send one message today to keep it alive.
              </p>
            )}

            <ul className="mt-4 space-y-2">
              {streak.milestones.map((m) => (
                <li
                  key={m.days}
                  data-testid={`streak-milestone-${m.days}`}
                  className={`flex items-center gap-2.5 text-sm ${
                    m.reached ? "text-ink" : "text-ink-soft"
                  }`}
                >
                  {m.reached ? (
                    <Check className="h-4 w-4 shrink-0 text-brand" />
                  ) : (
                    <Lock className="h-3.5 w-3.5 shrink-0 text-ink-soft/60" />
                  )}
                  <span>
                    <b>{m.days} days</b> · {m.label}
                  </span>
                </li>
              ))}
            </ul>

            {streak.badges.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="streak-badges">
                {streak.badges.map((b, i) => (
                  <span
                    key={`${b.badge}-${b.created_at}`}
                    data-testid={`streak-badge-${i}`}
                    className="rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand"
                  >
                    {b.badge}
                  </span>
                ))}
              </div>
            )}
            {streak.bonus_messages > 0 && (
              <p className="mt-3 text-xs text-ink-soft" data-testid="streak-bonus">
                +{streak.bonus_messages} bonus messages on your account
              </p>
            )}
            <p className="mt-3 text-xs text-ink-soft/70">
              Longest streak: {streak.longest} {streak.longest === 1 ? "day" : "days"}
            </p>

            {n > 0 && (
              <button
                data-testid="share-streak-open-panel"
                onClick={() => {
                  setOpen(false);
                  setSharing(true);
                }}
                className="mt-4 flex min-h-[40px] w-full items-center justify-center gap-2 rounded-full bg-brand px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
              >
                <Share2 className="h-4 w-4" /> Share my streak
              </button>
            )}

            {board?.top?.length > 0 && (
              <div className="mt-4 border-t border-white/10 pt-4" data-testid="streak-board-mini">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-soft">
                  <Trophy className="h-3.5 w-3.5 text-amber-300" /> Top streaks · {board.month}
                </p>
                <ol className="mt-2.5 space-y-1.5">
                  {board.top.slice(0, 5).map((p) => (
                    <li
                      key={p.rank}
                      data-testid={`streak-board-mini-${p.rank}`}
                      className={`flex items-center gap-2 text-sm ${
                        p.is_me ? "font-semibold text-brand" : "text-ink-soft"
                      }`}
                    >
                      <span className="w-4 text-xs text-ink-soft/70">{p.rank}</span>
                      <span className="flex-1 truncate" dir="auto">
                        {p.is_me ? "You" : p.name}
                      </span>
                      <span className="text-xs">{p.current}d</span>
                    </li>
                  ))}
                </ol>
                {board.me && board.me.rank > 5 && (
                  <p className="mt-2 text-xs text-ink-soft" data-testid="streak-my-rank">
                    You are #{board.me.rank} of {board.players}
                  </p>
                )}
              </div>
            )}

            <Link
              data-testid="streak-progress-link"
              to="/progress"
              onClick={() => setOpen(false)}
              className="mt-4 flex items-center gap-1.5 border-t border-white/10 pt-3 text-xs text-ink-soft transition-colors hover:text-brand"
            >
              <GraduationCap className="h-3.5 w-3.5" /> See my language practice
            </Link>
          </div>
        </>
      )}
      <ShareStreak open={sharing} onOpenChange={setSharing} />
    </div>
  );
};
